import React from 'react'
import {
    Box,
    FormControlLabel,
    IconButton,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Switch,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import {
    AVAILABILITY_LABELS,
    CONDITIONAL_METHOD_META,
    ConditionalApprovalsPolicy,
    DepositMode,
    EnhancedDepositConfig,
    ExtraRentMode,
    FallbackChoiceMode,
    MethodAvailability,
    QUALIFYING_METHOD_IDS,
    QualifyingMethodId,
    RentPrepayConfig,
    UpfrontRentMode,
    methodsForStage,
} from './conditionalApprovals'
import { FormSaveAction } from './FormSaveButton'
import { SetupArtifactShell } from './SetupArtifactShell'
import { SelectedSetupConfig, SetupConfigOption } from './setupArtifacts'

const useStyles = makeStyles((theme: Theme) => ({
    body: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        width: '100%',
        padding: theme.spacing(0.5, 0, 1),
    },
    intro: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
    },
    choiceCard: {
        padding: theme.spacing(1.25, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
    },
    choiceLabel: {
        fontWeight: 700,
        fontSize: '0.875rem',
        marginBottom: theme.spacing(0.5),
    },
    radioLabel: {
        '& .MuiFormControlLabel-label': {
            fontSize: '0.8125rem',
        },
    },
    methodCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
        padding: theme.spacing(1.25, 1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
    },
    methodTitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.25),
        minWidth: 0,
    },
    methodTitle: {
        fontWeight: 700,
        fontSize: '0.9375rem',
    },
    methodSummary: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
        marginTop: 2,
    },
    infoButton: {
        padding: 2,
        color: theme.palette.text.secondary,
        '& svg': { fontSize: 16 },
    },
    offeredBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(0.25),
    },
    switchLabel: {
        marginLeft: 0,
        marginRight: 0,
        '&.MuiFormControlLabel-labelPlacementStart': {
            marginLeft: 0,
            marginRight: 0,
        },
        '& .MuiFormControlLabel-label': {
            fontSize: '0.75rem',
            fontWeight: 600,
        },
    },
    fields: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        paddingTop: theme.spacing(0.5),
        borderTop: `1px dashed ${theme.palette.divider}`,
    },
    fieldRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
        gap: theme.spacing(1),
    },
    fieldLabel: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        width: '100%',
    },
    select: {
        minWidth: 180,
        fontSize: '0.8125rem',
    },
    availabilitySelect: {
        minWidth: 220,
        fontSize: '0.8125rem',
    },
    numberField: {
        width: 120,
        '& .MuiInputBase-input': {
            fontSize: '0.8125rem',
        },
    },
    docHint: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
        marginTop: 2,
    },
}))

function methodLabels(ids: QualifyingMethodId[]): string[] {
    return ids.map((id) => CONDITIONAL_METHOD_META[id].label)
}

function AvailabilityField({
    value,
    readOnly,
    onChange,
}: {
    value: MethodAvailability
    readOnly?: boolean
    onChange: (next: MethodAvailability) => void
}) {
    const classes = useStyles()
    return (
        <Box className={classes.fieldRow}>
            <Typography className={classes.fieldLabel}>When offered</Typography>
            <Select
                className={classes.availabilitySelect}
                value={value}
                disabled={readOnly}
                variant="outlined"
                margin="dense"
                onChange={(e) => onChange(e.target.value as MethodAvailability)}
            >
                {(Object.keys(AVAILABILITY_LABELS) as MethodAvailability[]).map(
                    (key) => (
                        <MenuItem key={key} value={key}>
                            {AVAILABILITY_LABELS[key]}
                        </MenuItem>
                    ),
                )}
            </Select>
        </Box>
    )
}

function MethodCard({
    methodId,
    enabled,
    availability,
    readOnly,
    onEnabledChange,
    onAvailabilityChange,
    children,
}: {
    methodId: QualifyingMethodId
    enabled: boolean
    availability: MethodAvailability
    readOnly?: boolean
    onEnabledChange: (enabled: boolean) => void
    onAvailabilityChange: (next: MethodAvailability) => void
    children?: React.ReactNode
}) {
    const classes = useStyles()
    const meta = CONDITIONAL_METHOD_META[methodId]
    return (
        <Box className={classes.methodCard}>
            <Box style={{ minWidth: 0 }}>
                <Box className={classes.methodTitleRow}>
                    <Typography className={classes.methodTitle}>
                        {meta.label}
                    </Typography>
                    <Tooltip title={meta.info} placement="top" arrow>
                        <IconButton
                            className={classes.infoButton}
                            size="small"
                            aria-label={`About ${meta.label}`}
                        >
                            <InfoOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Typography className={classes.methodSummary}>
                    {meta.summary}
                </Typography>
            </Box>
            <Box className={classes.offeredBlock}>
                <Typography className={classes.fieldLabel}>Offered</Typography>
                <FormControlLabel
                    className={classes.switchLabel}
                    control={
                        <Switch
                            color="primary"
                            size="small"
                            checked={enabled}
                            disabled={readOnly}
                            onChange={(_, checked) => onEnabledChange(checked)}
                        />
                    }
                    label={enabled ? 'On' : 'Off'}
                    labelPlacement="start"
                />
            </Box>
            {enabled ? (
                <Box className={classes.fields}>
                    <AvailabilityField
                        value={availability}
                        readOnly={readOnly}
                        onChange={onAvailabilityChange}
                    />
                    {children}
                </Box>
            ) : null}
        </Box>
    )
}

function EnhancedDepositFields({
    config,
    readOnly,
    onChange,
}: {
    config: EnhancedDepositConfig
    readOnly?: boolean
    onChange: (next: EnhancedDepositConfig) => void
}) {
    const classes = useStyles()
    return (
        <Box className={classes.fieldRow}>
            <Typography className={classes.fieldLabel}>
                Additional security deposit
            </Typography>
            <Select
                className={classes.select}
                value={config.depositMode}
                disabled={readOnly}
                variant="outlined"
                margin="dense"
                onChange={(e) =>
                    onChange({
                        ...config,
                        depositMode: e.target.value as DepositMode,
                    })
                }
            >
                <MenuItem value="rent_multiple">
                    Multiple of monthly rent
                </MenuItem>
                <MenuItem value="fixed">Fixed dollar amount</MenuItem>
            </Select>
            {config.depositMode === 'rent_multiple' ? (
                <TextField
                    className={classes.numberField}
                    label="× rent"
                    type="number"
                    size="small"
                    variant="outlined"
                    disabled={readOnly}
                    value={config.depositRentMultiple}
                    onChange={(e) =>
                        onChange({
                            ...config,
                            depositRentMultiple: Number(e.target.value) || 0,
                        })
                    }
                    inputProps={{ min: 0, step: 0.5 }}
                />
            ) : (
                <TextField
                    className={classes.numberField}
                    label="Amount ($)"
                    type="number"
                    size="small"
                    variant="outlined"
                    disabled={readOnly}
                    value={config.depositFixedAmount}
                    onChange={(e) =>
                        onChange({
                            ...config,
                            depositFixedAmount: e.target.value,
                        })
                    }
                    inputProps={{ min: 0 }}
                />
            )}
        </Box>
    )
}

function RentPrepayFields({
    config,
    readOnly,
    onChange,
}: {
    config: RentPrepayConfig
    readOnly?: boolean
    onChange: (next: RentPrepayConfig) => void
}) {
    const classes = useStyles()
    return (
        <>
            <Box className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>
                    Upfront rent
                </Typography>
                <Select
                    className={classes.select}
                    value={config.upfrontRent}
                    disabled={readOnly}
                    variant="outlined"
                    margin="dense"
                    onChange={(e) =>
                        onChange({
                            ...config,
                            upfrontRent: e.target.value as UpfrontRentMode,
                        })
                    }
                >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="first">First month</MenuItem>
                    <MenuItem value="first_and_last">
                        First and last month
                    </MenuItem>
                </Select>
            </Box>
            <Box className={classes.fieldRow}>
                <Typography className={classes.fieldLabel}>
                    Extra monthly rent
                </Typography>
                <Select
                    className={classes.select}
                    value={config.extraRentMode}
                    disabled={readOnly}
                    variant="outlined"
                    margin="dense"
                    onChange={(e) =>
                        onChange({
                            ...config,
                            extraRentMode: e.target.value as ExtraRentMode,
                        })
                    }
                >
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="fixed">Fixed dollar amount</MenuItem>
                    <MenuItem value="percent">Percent of rent</MenuItem>
                </Select>
                {config.extraRentMode === 'fixed' ? (
                    <TextField
                        className={classes.numberField}
                        label="Amount ($)"
                        type="number"
                        size="small"
                        variant="outlined"
                        disabled={readOnly}
                        value={config.extraRentFixed}
                        onChange={(e) =>
                            onChange({
                                ...config,
                                extraRentFixed: e.target.value,
                            })
                        }
                        inputProps={{ min: 0 }}
                    />
                ) : null}
                {config.extraRentMode === 'percent' ? (
                    <TextField
                        className={classes.numberField}
                        label="Percent"
                        type="number"
                        size="small"
                        variant="outlined"
                        disabled={readOnly}
                        value={config.extraRentPercent}
                        onChange={(e) =>
                            onChange({
                                ...config,
                                extraRentPercent: e.target.value,
                            })
                        }
                        inputProps={{ min: 0, max: 100 }}
                    />
                ) : null}
            </Box>
        </>
    )
}

export const ConditionalApprovalsSetupTab: React.FC<{
    selected: SelectedSetupConfig | null
    onSelected: (config: SelectedSetupConfig) => void
    onClearSelection: () => void
    existing: SetupConfigOption[]
    onExistingChange: (next: SetupConfigOption[]) => void
    policy: ConditionalApprovalsPolicy
    onPolicyChange: (next: ConditionalApprovalsPolicy) => void
    contentLocked?: boolean
    artifactEditable?: boolean
    saveAction?: FormSaveAction
}> = ({
    selected,
    onSelected,
    onClearSelection,
    existing,
    onExistingChange,
    policy,
    onPolicyChange,
    contentLocked = false,
    artifactEditable = true,
    saveAction,
}) => {
    const classes = useStyles()
    const readOnly =
        !!selected?.orgMandated || contentLocked || !artifactEditable

    const patchMethod = <K extends QualifyingMethodId>(
        id: K,
        patch: Partial<ConditionalApprovalsPolicy[K]>,
    ) => {
        if (readOnly) return
        onPolicyChange({
            ...policy,
            [id]: { ...policy[id], ...patch },
        })
    }

    const upfront = methodsForStage(policy, 'upfront')
    const afterFail = methodsForStage(policy, 'after_failed_screen')

    return (
        <SetupArtifactShell
            kind="conditional_approvals"
            selected={selected}
            onSelected={onSelected}
            onClearSelection={onClearSelection}
            existing={existing}
            onExistingChange={onExistingChange}
            contentLocked={contentLocked}
            summarySections={
                selected
                    ? [
                          {
                              title: 'Upfront options',
                              items: methodLabels(upfront),
                          },
                          {
                              title: 'After failed screening',
                              items: methodLabels(afterFail),
                          },
                      ]
                    : undefined
            }
            saveAction={readOnly ? undefined : saveAction}
        >
            <Box className={classes.body}>
                <Typography className={classes.intro}>
                    Enabled methods are the qualification options on the
                    application. Method-specific PDFs belong on the lease or
                    guarantor packet in Signable Documents.
                </Typography>
                {selected ? (
                    <Box className={classes.choiceCard}>
                        <Typography className={classes.choiceLabel}>
                            After failed screening, who chooses?
                        </Typography>
                        <RadioGroup
                            value={policy.fallbackChoiceMode}
                            onChange={(e) => {
                                if (readOnly) return
                                onPolicyChange({
                                    ...policy,
                                    fallbackChoiceMode: e.target
                                        .value as FallbackChoiceMode,
                                })
                            }}
                        >
                            <FormControlLabel
                                className={classes.radioLabel}
                                value="applicant"
                                control={<Radio color="primary" size="small" />}
                                disabled={readOnly}
                                label="Applicant chooses among after-fail methods"
                            />
                            <FormControlLabel
                                className={classes.radioLabel}
                                value="property_manager"
                                control={<Radio color="primary" size="small" />}
                                disabled={readOnly}
                                label="Property manager chooses (no auto offer to applicant)"
                            />
                        </RadioGroup>
                    </Box>
                ) : null}

                {QUALIFYING_METHOD_IDS.map((methodId) => {
                    const row = policy[methodId]
                    return (
                        <MethodCard
                            key={methodId}
                            methodId={methodId}
                            enabled={row.enabled}
                            availability={row.availability}
                            readOnly={readOnly}
                            onEnabledChange={(enabled) =>
                                patchMethod(methodId, { enabled } as Partial<
                                    ConditionalApprovalsPolicy[typeof methodId]
                                >)
                            }
                            onAvailabilityChange={(availability) =>
                                patchMethod(methodId, {
                                    availability,
                                } as Partial<ConditionalApprovalsPolicy[typeof methodId]>)
                            }
                        >
                            {methodId === 'enhanced_deposit' ? (
                                <EnhancedDepositFields
                                    config={policy.enhanced_deposit}
                                    readOnly={readOnly}
                                    onChange={(enhanced) =>
                                        patchMethod(
                                            'enhanced_deposit',
                                            enhanced,
                                        )
                                    }
                                />
                            ) : null}
                            {methodId === 'rent_prepay' ? (
                                <RentPrepayFields
                                    config={policy.rent_prepay}
                                    readOnly={readOnly}
                                    onChange={(rent) =>
                                        patchMethod('rent_prepay', rent)
                                    }
                                />
                            ) : null}
                        </MethodCard>
                    )
                })}
            </Box>
        </SetupArtifactShell>
    )
}
